import type { PortfolioReport } from '@interfaces/portfolioReport';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import type {
  RunReportImpactFilter,
  RunReportPortfolioFilters,
} from '../../types/runTable.types';
import { getAuditAccountTypeLabels } from '../audit/auditPresentation';
import {
  buildAuditByReportId,
  getPortfolioAuditPassedState,
  hasPortfolioValidationFailure,
  summarizePortfolioAudit,
} from '../audit/portfolioAuditSummary';
import {
  getReportClientLabel,
  getReportGoalLabel,
  getReportMarketValueCents,
} from '../presentation/reportLabels';
import {
  buildRunAttentionPortfolioRows,
  type RunAttentionBadge,
} from './buildRunAttentionPortfolioRows';
import {
  countRowsMatchingModel,
  matchesPortfolioFilters,
} from './runReportPortfolioFilters';

export type RunReportTableRow = {
  reportId: string;
  report?: PortfolioReport;
  audit?: OptimizerAuditV2Record;
  clientLabel: string;
  goalLabel: string;
  accountTypes: string[];
  modelLabel: string;
  modelPortfolioId?: string;
  marketValueCents: number;
  tradeCount: number;
  totalBuyCents: number;
  totalSellCents: number;
  passed?: boolean;
  unresolvedReport: boolean;
};

export type { RunReportImpactFilter } from '../../types/runTable.types';

export type RunReportTableSort =
  | 'impact'
  | 'marketValueDesc'
  | 'marketValueAsc'
  | 'tradesDesc'
  | 'tradesAsc';

export type RunReportTableSortDirection = 'asc' | 'desc';

export const resolveRunReportTableSortDirection = (
  sort: RunReportTableSort,
  ascSort: RunReportTableSort,
  descSort: RunReportTableSort,
): RunReportTableSortDirection | undefined => {
  if (sort === ascSort) {
    return 'asc';
  }

  if (sort === descSort) {
    return 'desc';
  }

  return undefined;
};

export type RunReportTableFilters = {
  impactFilter?: RunReportImpactFilter;
  portfolioFilters?: RunReportPortfolioFilters;
};

export type EnrichedRunReportTableRow = RunReportTableRow & {
  absDeltaCents: number;
  impactBadges: RunAttentionBadge[];
};

export type PrepareRunReportTableRowsOptions = RunReportTableFilters & {
  sort?: RunReportTableSort;
};

export type PrepareRunReportTableRowsResult = {
  rows: EnrichedRunReportTableRow[];
  matchingCount: number;
  globalMatchingCount?: number;
};

const getAuditMarketValueCents = (
  audit: OptimizerAuditV2Record | undefined,
): number =>
  Math.max(
    0,
    (audit?.subAccountResults ?? []).reduce(
      (sum, subAccount) =>
        sum + Math.max(0, subAccount.inputMarketValueCents ?? 0),
      0,
    ),
  );

const buildPortfolioRowLabels = (
  report: PortfolioReport | undefined,
): { clientLabel: string; goalLabel: string; unresolvedReport: boolean } => {
  if (report) {
    const clientLabel = getReportClientLabel(report);
    const goalLabel = getReportGoalLabel(report);

    if (clientLabel && goalLabel) {
      return { clientLabel, goalLabel, unresolvedReport: false };
    }

    return {
      clientLabel: clientLabel || goalLabel || report.id,
      goalLabel: clientLabel && goalLabel ? goalLabel : '',
      unresolvedReport: false,
    };
  }

  return {
    clientLabel: '',
    goalLabel: '',
    unresolvedReport: true,
  };
};

const buildRunReportTableRow = (
  translateBackendString: (translatedObject: unknown) => string,
  reportId: string,
  report: PortfolioReport | undefined,
  audit: OptimizerAuditV2Record | undefined,
): RunReportTableRow => {
  const labels = buildPortfolioRowLabels(report);
  const tradeSummary = hasPortfolioValidationFailure(audit)
    ? null
    : summarizePortfolioAudit(audit);

  return {
    reportId,
    report,
    audit,
    clientLabel: labels.clientLabel,
    goalLabel: labels.goalLabel,
    unresolvedReport: labels.unresolvedReport,
    accountTypes: getAuditAccountTypeLabels(audit, report),
    modelLabel: report
      ? translateBackendString(report.modelPortfolio?.translatedName)
      : '',
    modelPortfolioId: report?.modelPortfolio?.id,
    marketValueCents: report
      ? getReportMarketValueCents(report)
      : getAuditMarketValueCents(audit),
    tradeCount: tradeSummary?.tradeCount ?? 0,
    totalBuyCents: tradeSummary?.totalBuyCents ?? 0,
    totalSellCents: tradeSummary?.totalSellCents ?? 0,
    passed: getPortfolioAuditPassedState(audit),
  };
};

export const buildRunReportTableRows = (
  translateBackendString: (translatedObject: unknown) => string,
  reports: PortfolioReport[],
  audits: OptimizerAuditV2Record[],
): RunReportTableRow[] => {
  const auditByReportId = new Map<string, OptimizerAuditV2Record>();

  for (const audit of audits) {
    if (audit.portfolioReportId) {
      auditByReportId.set(audit.portfolioReportId, audit);
    }
  }

  return reports.map((report) =>
    buildRunReportTableRow(
      translateBackendString,
      report.id,
      report,
      auditByReportId.get(report.id),
    ),
  );
};

export const buildAllRunReportTableRows = (
  translateBackendString: (translatedObject: unknown) => string,
  reports: PortfolioReport[],
  audits: OptimizerAuditV2Record[],
): RunReportTableRow[] => {
  const reportsById = new Map(reports.map((report) => [report.id, report]));
  const auditByReportId = buildAuditByReportId(audits);
  const rowIds: string[] = [];

  for (const audit of audits) {
    if (audit.portfolioReportId && !rowIds.includes(audit.portfolioReportId)) {
      rowIds.push(audit.portfolioReportId);
    }
  }

  for (const report of reports) {
    if (!rowIds.includes(report.id)) {
      rowIds.push(report.id);
    }
  }

  return rowIds.map((reportId) =>
    buildRunReportTableRow(
      translateBackendString,
      reportId,
      reportsById.get(reportId),
      auditByReportId.get(reportId),
    ),
  );
};

const matchesImpactFilter = (
  row: EnrichedRunReportTableRow,
  impactFilter: RunReportImpactFilter,
): boolean => {
  switch (impactFilter) {
    case 'passed':
      return row.passed === true;
    case 'failed':
      return row.impactBadges.includes('failed');
    case 'liquidate':
      return row.impactBadges.includes('liquidate');
    case 'transfer':
      return row.impactBadges.includes('transfer');
    case 'tax':
      return row.impactBadges.includes('tax');
    case 'highTurnover':
      return row.impactBadges.includes('highTurnover');
    case 'all':
    default:
      return true;
  }
};

const matchesAllFilters = (
  row: EnrichedRunReportTableRow,
  impactFilter: RunReportImpactFilter,
  portfolioFilters: RunReportPortfolioFilters,
  audits: OptimizerAuditV2Record[],
  reportsById: Map<string, PortfolioReport>,
): boolean =>
  matchesImpactFilter(row, impactFilter) &&
  matchesPortfolioFilters(row, portfolioFilters, audits, reportsById);

const hydrateRunReportTableRow = (
  translateBackendString: (translatedObject: unknown) => string,
  row: RunReportTableRow,
  report: PortfolioReport | undefined,
  attentionBadges: RunAttentionBadge[],
  absDeltaCents: number,
): EnrichedRunReportTableRow => ({
  ...row,
  report: row.report ?? report,
  modelPortfolioId: row.modelPortfolioId ?? report?.modelPortfolio?.id,
  modelLabel:
    row.modelLabel ||
    (report
      ? translateBackendString(report.modelPortfolio?.translatedName)
      : ''),
  absDeltaCents,
  impactBadges: attentionBadges,
});

const compareNumbers = (
  a: number,
  b: number,
  direction: 'asc' | 'desc',
): number => (direction === 'asc' ? a - b : b - a);

const sortRunReportTableRows = (
  rows: EnrichedRunReportTableRow[],
  sort: RunReportTableSort,
): EnrichedRunReportTableRow[] =>
  [...rows].sort((a, b) => {
    switch (sort) {
      case 'marketValueDesc':
        return compareNumbers(a.marketValueCents, b.marketValueCents, 'desc');
      case 'marketValueAsc':
        return compareNumbers(a.marketValueCents, b.marketValueCents, 'asc');
      case 'tradesDesc': {
        const tradeCountDelta = compareNumbers(
          a.tradeCount,
          b.tradeCount,
          'desc',
        );
        if (tradeCountDelta !== 0) {
          return tradeCountDelta;
        }

        return compareNumbers(
          a.totalBuyCents + a.totalSellCents,
          b.totalBuyCents + b.totalSellCents,
          'desc',
        );
      }
      case 'tradesAsc': {
        const tradeCountDelta = compareNumbers(
          a.tradeCount,
          b.tradeCount,
          'asc',
        );
        if (tradeCountDelta !== 0) {
          return tradeCountDelta;
        }

        return compareNumbers(
          a.totalBuyCents + a.totalSellCents,
          b.totalBuyCents + b.totalSellCents,
          'asc',
        );
      }
      case 'impact':
      default: {
        const impactDelta = compareNumbers(
          a.absDeltaCents,
          b.absDeltaCents,
          'desc',
        );
        if (impactDelta !== 0) {
          return impactDelta;
        }

        return compareNumbers(a.tradeCount, b.tradeCount, 'desc');
      }
    }
  });

export const prepareRunReportTableRows = (
  translateBackendString: (translatedObject: unknown) => string,
  reports: PortfolioReport[],
  audits: OptimizerAuditV2Record[],
  options: PrepareRunReportTableRowsOptions = {},
): PrepareRunReportTableRowsResult => {
  const {
    impactFilter = 'all',
    portfolioFilters = {},
    sort = 'impact',
  } = options;
  const reportsById = new Map(reports.map((report) => [report.id, report]));
  const baseRows = buildAllRunReportTableRows(
    translateBackendString,
    reports,
    audits,
  );
  const attentionByReportId = new Map(
    buildRunAttentionPortfolioRows(
      translateBackendString,
      audits,
      reportsById,
      {
        limit: Infinity,
      },
    ).map((row) => [row.reportId, row]),
  );

  const enrichedRows = baseRows.map((row) => {
    const report = reportsById.get(row.reportId);
    const attention = attentionByReportId.get(row.reportId);

    return hydrateRunReportTableRow(
      translateBackendString,
      row,
      report,
      attention?.badges ?? [],
      attention?.absDeltaCents ?? 0,
    );
  });

  const filteredRows = enrichedRows.filter((row) =>
    matchesAllFilters(row, impactFilter, portfolioFilters, audits, reportsById),
  );

  const globalMatchingCount = portfolioFilters.financialProductId
    ? enrichedRows.filter(
        (row) =>
          matchesImpactFilter(row, impactFilter) &&
          matchesPortfolioFilters(
            row,
            {
              modelPortfolioId: portfolioFilters.modelPortfolioId,
              financialProductId: portfolioFilters.financialProductId,
            },
            audits,
            reportsById,
          ),
      ).length
    : portfolioFilters.modelPortfolioId
      ? countRowsMatchingModel(
          enrichedRows.filter((row) => matchesImpactFilter(row, impactFilter)),
          portfolioFilters.modelPortfolioId,
          reportsById,
        )
      : undefined;

  const sortedRows = sortRunReportTableRows(filteredRows, sort);

  return {
    rows: sortedRows,
    matchingCount: filteredRows.length,
    globalMatchingCount,
  };
};
